import type { IMenuItem } from '../WorkspaceMenu';
import ModernGuestList from '../routes/ModernGuestList';
import WebsitePreview from '../widgets/WebsitePreview';
import type { IDashboardComponent } from '../WorkspaceGrid';
import { EventCountdown } from '../widgets/EventCountdown';
import { Concierge } from '../widgets/Concierge';
import { EventType } from '@graphql/generated-base';
import { useAuth } from '@shared/components/AuthProvider';
import { useMemo } from 'react';
import type { FormatTypeformLinkParams } from '../utils/formatTypeformLink';
import { formatTypeformLink } from '../utils/formatTypeformLink';
import { useEventInfo } from '@shared/utils/eventInfo';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';
import { useGeneralDefaultConfig } from '../configuration/general_default';
import { useWeddingDefaultConfig } from '../configuration/wedding_default';
import { useBabyDefaultConfig } from '../configuration/baby_default';
import { useCarbonDefaultConfig } from '../configuration/carbon_default';

export interface IWorkspaceTile {
  readonly componentId: 'event_countdown' | 'guest_list' | 'website' | 'concierge';
  readonly allowScroll?: boolean;
  readonly route?: string;
  readonly link?: string;
}
export interface IAppConfig extends IMenuItem {}
export interface IEventSubTypeConfiguration {
  readonly category: string;
  readonly categorySubType: string;
  readonly title: string;
  readonly description: string;
  readonly insights: readonly string[];
  readonly location: {
    readonly city: string;
    readonly state: string;
    readonly country: string;
  };
  readonly date: string;
  readonly eventImage: string;
  readonly eventIcon: string;
  readonly apps: readonly IAppConfig[];
  readonly tiles: readonly IWorkspaceTile[];
}

const getAppTileComponent = (appId?: IWorkspaceTile['componentId']) => {
  switch (appId) {
    case 'guest_list':
      return ModernGuestList;
    case 'website':
      return WebsitePreview;
    case 'event_countdown':
      return EventCountdown;
    case 'concierge':
      return Concierge;
    default:
      return;
  }
};

export const EVENT_HANDLE_PLACEHOLDER = '${eventHandle}';

const formatLink = (link?: string, eventHandle?: string, typeformLinkParams?: Omit<FormatTypeformLinkParams, 'link'>) => {
  if (!link || !eventHandle) {
    return undefined;
  }

  if (link.startsWith('/')) {
    // Assume link is a relative path that starts after .com/ that does NOT contain the event handle
    return `/${eventHandle}${link}`;
  } else if (link.startsWith(EVENT_HANDLE_PLACEHOLDER)) {
    // Assume link is a relative path that starts after the event handle
    return link.replace(EVENT_HANDLE_PLACEHOLDER, `/${eventHandle}`);
  } else if (link.startsWith('https://withjoy.typeform.com')) {
    // Special case for concierge link to append event data as hash params
    return formatTypeformLink({
      link,
      ...typeformLinkParams,
    });
  }
  // Assume external link
  return link;
};

const formatRoute = (route?: string, eventHandle?: string) => {
  // Prepend event handle for internal routes
  return route && eventHandle ? `/${eventHandle}${route}` : undefined;
};
/*
Calculates the width of the parent container and updates it on resize
to enable child to have responsive behavior
not tied to browser window width
*/
export const useEventSubTypeConfig = (eventType: EventType, eventHandle: string) => {
  // Example initialization for navigation and tiles

  const { currentUser } = useAuth();
  const { eventInfo } = useEventInfo();
  const { eventId, locationInfo } = eventInfo || {};

  const { email, firstName, lastName } = currentUser.profile || {};

  const typeformLinkParams: Omit<FormatTypeformLinkParams, 'link'> = useMemo(
    () => ({
      email: email || '',
      firstName: firstName || '',
      lastName: lastName || '',
      eventId: eventId || '',
      locationId: locationInfo?.placeId || '',
    }),
    [email, firstName, lastName, eventId, locationInfo?.placeId],
  );
  const useCarbon = useProFeature(EventFeature.carbonEnabled);
  const typeOverride = useCarbon ? 'carbon' : eventType;
  const weddingConfig = useWeddingDefaultConfig();
  const generalConfig = useGeneralDefaultConfig();
  const babyConfig = useBabyDefaultConfig();
  const carbonConfig = useCarbonDefaultConfig();
  const appConfig = useMemo<IEventSubTypeConfiguration>(() => {
    switch (typeOverride) {
      case EventType.wedding:
        return weddingConfig;
      case EventType.babyRegistry:
        return babyConfig;
      case EventType.general:
        return generalConfig;
      case EventType.card:
        return generalConfig;
      case 'carbon':
        return carbonConfig;
      default:
        return generalConfig;
    }
  }, [babyConfig, carbonConfig, generalConfig, typeOverride, weddingConfig]);

  const navigation: IAppConfig[] = useMemo(
    () =>
      appConfig.apps.map(
        item =>
          ({
            ...item,
            route: formatRoute(item.route, eventHandle),
            link: formatLink(item.link, eventHandle, typeformLinkParams),
          }) as IAppConfig,
      ),
    [appConfig.apps, eventHandle, typeformLinkParams],
  );

  const tiles: IDashboardComponent[] = useMemo(
    () =>
      appConfig.tiles.map(item => ({
        instanceId: item.componentId,
        component: getAppTileComponent(item.componentId),
        colSpan: 1,
        allowScroll: item.allowScroll,
        route: formatRoute(item.route, eventHandle),
        link: formatLink(item.link, eventHandle, typeformLinkParams),
      })),
    [appConfig.tiles, eventHandle, typeformLinkParams],
  );

  return { navigation, tiles };
};
